import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, FileSpreadsheet, File as FileIcon, Folder, FolderPlus,
  Upload, Download, Trash2, Copy, Eye, ChevronLeft, ChevronRight, Home, Bot, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const enconOrange = '#ee7203';
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getFileIcon = (contentType) => {
  if (contentType === 'application/pdf') return { Icon: FileText, color: '#dc2626' };
  if (contentType?.includes('spreadsheet') || contentType?.includes('csv') || contentType?.includes('excel')) return { Icon: FileSpreadsheet, color: '#16a34a' };
  if (contentType?.includes('word') || contentType?.includes('document')) return { Icon: FileText, color: '#2563eb' };
  return { Icon: FileIcon, color: '#6b7280' };
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const canPreviewInline = (contentType) => contentType === 'application/pdf' || contentType?.startsWith('image/');

const ItemCard = ({ item, onOpen, onPreview, onDownload, onDelete, onCopy, onDropInto, dragItemId, setDragItemId }) => {
  const isFolder = item.type === 'folder';
  const { Icon, color } = isFolder ? { Icon: Folder, color: enconOrange } : getFileIcon(item.content_type);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => { setDragItemId(item.id); e.dataTransfer.setData('text/plain', item.id); }}
      onDragEnd={() => setDragItemId(null)}
      onDragOver={(e) => { if (isFolder && dragItemId && dragItemId !== item.id) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (isFolder && dragItemId && dragItemId !== item.id) onDropInto(dragItemId, item.id);
      }}
      onClick={() => isFolder && onOpen(item)}
      className={`group relative rounded-xl border p-3 flex flex-col gap-2 cursor-pointer transition-all ${dragOver ? 'border-orange-400 bg-orange-50 scale-[1.02]' : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'}`}
      data-testid={`reports-hub-item-${item.id}`}
    >
      <div className="flex items-start justify-between">
        <Icon className="w-9 h-9 flex-shrink-0" style={{ color }} />
        {item.source === 'claude_mcp' && (
          <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold" data-testid={`reports-hub-claude-badge-${item.id}`}>
            <Bot className="w-2.5 h-2.5" />Claude
          </span>
        )}
      </div>
      <p className="text-xs font-semibold text-gray-800 leading-tight break-words line-clamp-2" title={item.name}>{item.name}</p>
      <p className="text-[10px] text-gray-400">
        {isFolder ? 'Folder' : formatSize(item.size_bytes)} · {new Date(item.created_at).toLocaleDateString('en-GB')}
      </p>

      {!isFolder && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 bg-white/95 rounded-lg p-1 shadow-sm">
          <button onClick={(e) => { e.stopPropagation(); onPreview(item); }} className="p-1 hover:bg-gray-100 rounded" data-testid={`reports-hub-preview-btn-${item.id}`}><Eye className="w-3.5 h-3.5 text-gray-600" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDownload(item); }} className="p-1 hover:bg-gray-100 rounded" data-testid={`reports-hub-download-btn-${item.id}`}><Download className="w-3.5 h-3.5 text-gray-600" /></button>
          <button onClick={(e) => { e.stopPropagation(); onCopy(item); }} className="p-1 hover:bg-gray-100 rounded" data-testid={`reports-hub-copy-btn-${item.id}`}><Copy className="w-3.5 h-3.5 text-gray-600" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(item); }} className="p-1 hover:bg-red-50 rounded" data-testid={`reports-hub-delete-btn-${item.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
        </div>
      )}
      {isFolder && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(item); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded bg-white/95 shadow-sm" data-testid={`reports-hub-delete-btn-${item.id}`}>
          <Trash2 className="w-3.5 h-3.5 text-red-500" />
        </button>
      )}
    </div>
  );
};

const ReportsHub = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [parentId, setParentId] = useState(null);
  const [items, setItems] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragItemId, setDragItemId] = useState(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [previewItem, setPreviewItem] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/reports-hub/items`, { params: { year, month, parent_id: parentId || undefined } });
      setItems(res.data.items || []);
      setBreadcrumb(res.data.breadcrumb || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [year, month, parentId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const changeMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear += 1; }
    if (newMonth < 1) { newMonth = 12; newYear -= 1; }
    setMonth(newMonth); setYear(newYear); setParentId(null);
  };

  const uploadFiles = async (fileList) => {
    const allowed = ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx'];
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!allowed.includes(ext)) {
          toast.error(`${file.name}: file type not allowed`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('year', year);
        formData.append('month', month);
        if (parentId) formData.append('parent_id', parentId);
        await api.post(`${API}/reports-hub/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Upload complete');
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) { toast.error('Folder name is required'); return; }
    try {
      await api.post(`${API}/reports-hub/folders`, { year, month, parent_id: parentId, name: newFolderName.trim() });
      toast.success('Folder created');
      setShowNewFolder(false);
      setNewFolderName('');
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"${item.type === 'folder' ? ' and everything inside it' : ''}?`)) return;
    try {
      await api.delete(`${API}/reports-hub/items/${item.id}`);
      toast.success('Deleted');
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCopy = async (item) => {
    try {
      await api.post(`${API}/reports-hub/items/${item.id}/copy`, {});
      toast.success('Copied');
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to copy');
    }
  };

  const fetchBlob = async (item) => {
    const res = await api.get(`${API}/reports-hub/items/${item.id}/content`, { responseType: 'blob' });
    return new Blob([res.data], { type: item.content_type });
  };

  const handleDownload = async (item) => {
    try {
      const blob = await fetchBlob(item);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = item.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download');
    }
  };

  const handlePreview = async (item) => {
    if (!canPreviewInline(item.content_type)) {
      toast.info('Preview not available for this file type — downloading instead');
      handleDownload(item);
      return;
    }
    try {
      const blob = await fetchBlob(item);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewItem(item);
    } catch (e) {
      toast.error('Failed to preview');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewItem(null);
  };

  const handleDropInto = async (draggedId, targetFolderId) => {
    try {
      await api.put(`${API}/reports-hub/items/${draggedId}/move`, { parent_id: targetFolderId, year, month });
      toast.success('Moved');
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to move');
    }
  };

  const handleGridDrop = (e) => {
    e.preventDefault();
    setDropZoneActive(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const openFolder = (item) => setParentId(item.id);
  const goToBreadcrumb = (id) => setParentId(id);

  const subHeaderLeft = (
    <>
      <button onClick={() => navigate('/')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors" style={{ color: enconOrange }} data-testid="reports-hub-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /><span className="hidden sm:inline">Back</span>
      </button>
      <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <Folder className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
        <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Reports</span>
      </div>
    </>
  );

  const subHeaderRight = (
    <div className="flex items-center gap-1.5">
      <button onClick={() => changeMonth(-1)} className="p-1.5 rounded-full bg-white/90 hover:bg-white" data-testid="reports-hub-prev-month-btn"><ChevronLeft className="w-3.5 h-3.5" style={{ color: enconOrange }} /></button>
      <Select value={String(month)} onValueChange={(v) => { setMonth(Number(v)); setParentId(null); }}>
        <SelectTrigger className="h-7 text-xs w-28 bg-white/90 border-0" data-testid="reports-hub-month-select"><SelectValue /></SelectTrigger>
        <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => { setYear(Number(v)); setParentId(null); }}>
        <SelectTrigger className="h-7 text-xs w-20 bg-white/90 border-0" data-testid="reports-hub-year-select"><SelectValue /></SelectTrigger>
        <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
      </Select>
      <button onClick={() => changeMonth(1)} className="p-1.5 rounded-full bg-white/90 hover:bg-white" data-testid="reports-hub-next-month-btn"><ChevronRight className="w-3.5 h-3.5" style={{ color: enconOrange }} /></button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <AppHeader user={user} onLogout={onLogout} subHeaderLeft={subHeaderLeft} subHeaderRight={subHeaderRight} />

      <div className="px-4 lg:px-8 py-6" data-testid="reports-hub-page">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-1.5 text-sm text-gray-600 flex-wrap" data-testid="reports-hub-breadcrumb">
            <button onClick={() => setParentId(null)} className="flex items-center gap-1 font-semibold hover:underline" style={{ color: enconOrange }}>
              <Home className="w-3.5 h-3.5" />{MONTH_NAMES[month - 1]} {year}
            </button>
            {breadcrumb.map((b) => (
              <span key={b.id} className="flex items-center gap-1.5">
                <span className="text-gray-300">/</span>
                <button onClick={() => goToBreadcrumb(b.id)} className="hover:underline font-medium">{b.name}</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowNewFolder(true)} data-testid="reports-hub-new-folder-btn">
              <FolderPlus className="w-3.5 h-3.5 mr-1.5" />New Folder
            </Button>
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ backgroundColor: enconOrange }} data-testid="reports-hub-upload-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}Upload
            </Button>
            <input ref={fileInputRef} type="file" multiple hidden accept=".pdf,.xlsx,.xls,.csv,.doc,.docx" onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)} data-testid="reports-hub-file-input" />
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer.types.includes('Files')) setDropZoneActive(true); }}
          onDragLeave={() => setDropZoneActive(false)}
          onDrop={handleGridDrop}
          className={`min-h-[300px] rounded-2xl border-2 border-dashed transition-colors p-4 ${dropZoneActive ? 'border-orange-400 bg-orange-50' : 'border-transparent'}`}
          data-testid="reports-hub-dropzone"
        >
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-2" data-testid="reports-hub-empty-state">
              <Folder className="w-10 h-10" />
              <p className="text-sm">No files or folders yet for {MONTH_NAMES[month - 1]} {year}</p>
              <p className="text-xs">Drag and drop files here, or use Upload / New Folder above</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="reports-hub-items-grid">
              {items.map((item) => (
                <ItemCard
                  key={item.id} item={item} onOpen={openFolder} onPreview={handlePreview}
                  onDownload={handleDownload} onDelete={handleDelete} onCopy={handleCopy}
                  onDropInto={handleDropInto} dragItemId={dragItemId} setDragItemId={setDragItemId}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent data-testid="reports-hub-new-folder-dialog">
          <DialogHeader><DialogTitle>New Folder</DialogTitle></DialogHeader>
          <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Folder name" autoFocus data-testid="reports-hub-new-folder-input" onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowNewFolder(false)} data-testid="reports-hub-new-folder-cancel-btn">Cancel</Button>
            <Button onClick={handleCreateFolder} style={{ backgroundColor: enconOrange }} data-testid="reports-hub-new-folder-create-btn">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewItem} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="reports-hub-preview-dialog">
          <DialogHeader className="flex-row items-center justify-between">
            <DialogTitle className="truncate">{previewItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {previewUrl && previewItem?.content_type === 'application/pdf' && (
              <iframe src={previewUrl} title="Preview" className="w-full h-full border-0" data-testid="reports-hub-preview-iframe" />
            )}
            {previewUrl && previewItem?.content_type?.startsWith('image/') && (
              <img src={previewUrl} alt={previewItem?.name} className="max-w-full max-h-full mx-auto object-contain" data-testid="reports-hub-preview-image" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsHub;
